import { useEffect, useRef, useState } from "react";
import RFlatList from "../../../RFlatList/RFlatList";
import RacecardAndResultWidgetSimple from "./RacecardAndResultWidgetSimple";
import { StyleSheet, View } from "react-native";
import { FlatList } from "react-native-gesture-handler";
import RHeading from "../../../RHeading/RHeading";
import Icon from "../../../Icon";
import { formatFriendlyLongDate } from "../../../../utils/TimeCalculator";
import Gap from "../../../Gap";
import { fetchGet } from "../../../../client";
import { APIResponse } from "../../../../types/responses/APIResponse";
import RacecardAndResultWidgetLoader from "../../../States/Loading/Loaders/cms/RacecardAndResultWidgetLoader";

/**
 * RacecardAndResultWidgetSingleDay is an attachment component that displays the racecard and results for a
 * racecourse on a single day. It fetches data from the API using the provided ID and displays it in a horizontal scroll bar
 *
 * It also shows the date as a header, unless it's being used within the multi-day widget, in which case the meeting is shown
 * in the tab label and not repeated here
 *
 * The width and height of the widgets in the scroll bar, as well as the height of the scroll bar itself, have to be fixed
 * to ensure the correct snapping behaviour and to prevent layout issues with the horizontal scroll
 *
 * @param {string} id - the ID of the single day attachment to fetch data for
 * @param {any} dataOverride - optional prop to directly provide data instead of fetching from API (used for multi-day widget)
 * @param {boolean} displayDateHeading - whether to display the date heading (default: true, set to false when used in multi-day widget)
 *
 * @returns {JSX.Element} The rendered component, or null if there's an error or no data
 */

type Props = {
  id?: string;
  dataOverride?: any;
  displayDateHeading?: boolean;
  multiday?: boolean; // for analytics to know if this is being rendered within the multi-day widget
};

const RacecardAndResultWidgetSingleDay = ({
  id,
  dataOverride,
  displayDateHeading = true,
  multiday = false, // for analytics to know if this is being rendered within the multi-day widget
}: Props) => {
  const scrollRef = useRef<FlatList>(null);
  const [data, setData] = useState<any>(null); //TODO: update type once API is ready
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (dataOverride) {
      setData(dataOverride);
      setLoading(false);
      return;
    }
    if (!id) {
      setLoading(false);
      return;
    }
    const fetchData = async () => {
      try {
        const res: APIResponse<{ data: any }> = await fetchGet(
          `widgets/racecard-and-result-widget/${id}`
        );
        if (res.data) {
          setData(res.data);
        } else setError("No data found");
      } catch (err) {
        setError(
          "Error loading race: " +
            (err instanceof Error ? err.message : "Unknown")
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  if (loading)
    return (
      <View style={{ width: "100%" }}>
        <RacecardAndResultWidgetLoader />
      </View>
    );

  if (error || !data) return null;

  const { meeting } = data[0] || {};

  const meetingDate = meeting?.date;
  const countryCode = meeting?.track?.country_code?.iso;
  const weatherIcon = meeting?.weather_icon;
  const formattedDate = meetingDate
    ? formatFriendlyLongDate(new Date(meetingDate))
    : "";

  return (
    <View style={{ gap: 10 }}>
      {displayDateHeading && (
        <View style={styles.dateHeader}>
          <RHeading level="3" boldText uppercase>
            {formattedDate}
          </RHeading>
          {countryCode && (
            <>
              <Gap size="xsmall" />
              <Icon name={`flag_${countryCode}`} size="medium" />
            </>
          )}
          {weatherIcon && <Icon name={weatherIcon} size="large" />}
        </View>
      )}
      <RFlatList
        elementDistance={308}
        heightOverride={204}
        horizontal
        scrollRef={scrollRef}
        data={data}
        keyExtractor={(_, index) =>
          `r-and-r-widget-${index}-${meetingDate}-${meeting?.track?.id}`
        }
        renderItem={({ item }: { item: any }) => {
          const isResult = item?.status?.state === "resulted";
          const isRacecard = !isResult;
          return (
            <View style={[styles.container, isResult ? {} : styles.greyBorder]}>
              <RacecardAndResultWidgetSimple
                data={item}
                isRacecard={isRacecard}
                isSingleRace={false}
                multiday={multiday}
              />
            </View>
          );
        }}
      ></RFlatList>
    </View>
  );
};

export default RacecardAndResultWidgetSingleDay;

const styles = StyleSheet.create({
  dateHeader: {
    flexDirection: "row",
    alignItems: "center",
  },
  container: {
    marginRight: 8,
    width: 300,
    aspectRatio: 300 / 204,
  },
  greyBorder: {
    borderColor: "#E1E1E1",
    borderStyle: "solid",
    borderRadius: 8,
  },
});
